import { sha256Hex } from "../integration/local-ifc";
import { canonicalJson, canonicalJsonBytes } from "./d2-property-proposal-candidate";
import type { AnonymousFixture, Classification } from "./types";

export const D2_LOCAL_CLASSIFICATION_PROPOSAL_VERSION = "workbench-local-classification-proposal/1" as const;
export const D2_LOCAL_CLASSIFICATION_TARGET_CAP = 1_024 as const;

export interface D2LocalClassificationReference extends Classification {
  readonly edition?: string;
  readonly uri?: string;
}

export interface D2LocalClassificationTargetInput {
  readonly elementId: string;
  readonly before: Classification | null;
  readonly after: D2LocalClassificationReference;
}

export type D2LocalClassificationRefusalReason =
  | "duplicate-target"
  | "missing-element"
  | "read-only-element"
  | "before-mismatch"
  | "invalid-reference"
  | "already-satisfied"
  | "population-refused";

export interface D2LocalClassificationOperation {
  readonly opId: string;
  readonly order: number;
  readonly kind: "classification-map";
  readonly elementId: string;
  readonly before: Classification | null;
  readonly after: D2LocalClassificationReference;
  readonly writerAdmission: "unsupported-current-provider";
  readonly catalogAuthorityClaim: false;
}

export interface D2LocalClassificationProposal {
  readonly contractVersion: typeof D2_LOCAL_CLASSIFICATION_PROPOSAL_VERSION;
  readonly status: "local-empty" | "local-proposal" | "local-refusal";
  readonly authority: "none";
  readonly source: {
    readonly datasetId: string;
    readonly byteLength: number;
    readonly sha256: string;
    readonly bytesPreserved: true;
  };
  readonly selectedPopulation: {
    readonly selected: number;
    readonly proposed: number;
    readonly refused: number;
    readonly exactClosure: true;
    readonly checkedCap: typeof D2_LOCAL_CLASSIFICATION_TARGET_CAP;
    readonly checkedCapIsProductionLimit: false;
    readonly silentPartialApply: false;
  };
  readonly targetOutcomes: readonly {
    readonly elementId: string;
    readonly status: "proposed" | "refused";
    readonly refusalReason?: D2LocalClassificationRefusalReason;
  }[];
  readonly operations: readonly D2LocalClassificationOperation[];
  readonly writerAdmission: {
    readonly supported: 0;
    readonly unsupported: number;
    readonly unsupportedOperationsWereApplied: false;
  };
  readonly validation: { readonly state: "partial"; readonly authoritativeVerdict: false };
  readonly preservationAssertions: {
    readonly sourceBytesUnchanged: true;
    readonly fixtureUnchanged: true;
    readonly unknownRecordsRetained: true;
  };
}

export interface D2LocalClassificationAuthoringInput {
  readonly fixture: AnonymousFixture;
  readonly source: { readonly byteLength: number; readonly sha256: string };
  readonly targets: readonly D2LocalClassificationTargetInput[];
}

const SOURCE_DIGEST = /^[0-9a-f]{64}$/;

function normalizedReference(reference: D2LocalClassificationReference): D2LocalClassificationReference {
  return {
    system: reference.system.trim(),
    code: reference.code.trim(),
    label: reference.label.trim(),
    ...(reference.edition === undefined ? {} : { edition: reference.edition.trim() }),
    ...(reference.uri === undefined ? {} : { uri: reference.uri.trim() })
  };
}

function validReference(reference: D2LocalClassificationReference): boolean {
  return reference.system !== "" && reference.code !== "" && reference.label !== "" &&
    (reference.edition === undefined || reference.edition !== "") &&
    (reference.uri === undefined || reference.uri !== "");
}

function stableTargets(targets: readonly D2LocalClassificationTargetInput[]): readonly D2LocalClassificationTargetInput[] {
  return [...targets].sort((left, right) => left.elementId.localeCompare(right.elementId));
}

function duplicateIndexes(targets: readonly D2LocalClassificationTargetInput[]): ReadonlySet<number> {
  const firstIndexByElement = new Map<string, number>();
  const duplicates = new Set<number>();
  targets.forEach((target, index) => {
    const firstIndex = firstIndexByElement.get(target.elementId);
    if (firstIndex === undefined) firstIndexByElement.set(target.elementId, index);
    else {
      duplicates.add(firstIndex);
      duplicates.add(index);
    }
  });
  return duplicates;
}

function validateTarget(
  fixture: AnonymousFixture,
  target: D2LocalClassificationTargetInput
): D2LocalClassificationRefusalReason | null {
  const element = fixture.elements.find((candidate) => candidate.id === target.elementId);
  if (element === undefined) return "missing-element";
  if (!element.editable || element.unsupportedReason !== undefined) return "read-only-element";
  if (canonicalJson(element.classification) !== canonicalJson(target.before)) return "before-mismatch";
  const after = normalizedReference(target.after);
  if (!validReference(after)) return "invalid-reference";
  if (canonicalJson(target.before) === canonicalJson(after)) return "already-satisfied";
  return null;
}

export function assertD2LocalClassificationTargetCount(count: number): void {
  if (!Number.isSafeInteger(count) || count < 0) {
    throw new Error("Local classification target count must be a non-negative safe integer.");
  }
  if (count > D2_LOCAL_CLASSIFICATION_TARGET_CAP) {
    throw new Error(`Local classification proposal exceeds the checked cap of ${D2_LOCAL_CLASSIFICATION_TARGET_CAP} targets.`);
  }
}

export async function authorD2LocalClassificationProposal(
  input: D2LocalClassificationAuthoringInput
): Promise<D2LocalClassificationProposal> {
  assertD2LocalClassificationTargetCount(input.targets.length);
  if (!Number.isSafeInteger(input.source.byteLength) || input.source.byteLength < 0 || !SOURCE_DIGEST.test(input.source.sha256)) {
    throw new Error("Local classification source receipt is invalid.");
  }
  const targets = stableTargets(input.targets);
  const duplicates = duplicateIndexes(targets);
  const reasons = new Map<number, D2LocalClassificationRefusalReason>();
  targets.forEach((target, index) => {
    const reason = duplicates.has(index) ? "duplicate-target" : validateTarget(input.fixture, target);
    if (reason !== null) reasons.set(index, reason);
  });
  const refused = reasons.size > 0;
  const empty = targets.length === 0;
  const operations = refused ? [] : await Promise.all(targets.map(async (target, order): Promise<D2LocalClassificationOperation> => {
    const after = normalizedReference(target.after);
    return {
      opId: `op-sha256-${await sha256Hex(canonicalJsonBytes({
        sourceSha256: input.source.sha256,
        datasetId: input.fixture.id,
        elementId: target.elementId,
        kind: "classification-map",
        before: target.before,
        after
      }))}`,
      order,
      kind: "classification-map",
      elementId: target.elementId,
      before: structuredClone(target.before),
      after: structuredClone(after),
      writerAdmission: "unsupported-current-provider",
      catalogAuthorityClaim: false
    };
  }));
  const targetOutcomes = targets.map((target, index) => ({
    elementId: target.elementId,
    status: refused ? "refused" as const : "proposed" as const,
    ...(refused ? { refusalReason: reasons.get(index) ?? "population-refused" as const } : {})
  }));

  return {
    contractVersion: D2_LOCAL_CLASSIFICATION_PROPOSAL_VERSION,
    status: empty ? "local-empty" : refused ? "local-refusal" : "local-proposal",
    authority: "none",
    source: {
      datasetId: input.fixture.id,
      byteLength: input.source.byteLength,
      sha256: input.source.sha256,
      bytesPreserved: true
    },
    selectedPopulation: {
      selected: targets.length,
      proposed: refused ? 0 : targets.length,
      refused: refused ? targets.length : 0,
      exactClosure: true,
      checkedCap: D2_LOCAL_CLASSIFICATION_TARGET_CAP,
      checkedCapIsProductionLimit: false,
      silentPartialApply: false
    },
    targetOutcomes,
    operations,
    writerAdmission: {
      supported: 0,
      unsupported: operations.length,
      unsupportedOperationsWereApplied: false
    },
    validation: { state: "partial", authoritativeVerdict: false },
    preservationAssertions: {
      sourceBytesUnchanged: true,
      fixtureUnchanged: true,
      unknownRecordsRetained: true
    }
  };
}
